import {
    cancelAllUpload as cancelAllSdkUpload,
    uploadFile as uploadFileToOss,
    uploadForBase64,
    uploadForText,
} from './oss-sdk-client';

type UploadProgressCallback = (percent: number) => void;

interface UploadResponse {
    code: number;
    msg: string;
    data?: { url: string };
}

interface UploadSourceResult {
    file: File;
    sourceUrl?: string;
}

export interface OssUploadCredentials {
    aliyun: {
        accessKeyId: string;
        accessKeySecret: string;
        expiration: string;
        securityToken: string;
    };
    bucket: string;
    domain: string;
    endpoint: string;
    expired: number;
    prefix: string;
    region: string;
}

export function cancelAllUpload() {
    cancelAllSdkUpload();
}

function isRemoteUrl(value: string) {
    return /^https?:\/\//i.test(value);
}

function isDataUrl(value: string) {
    return value.startsWith('data:');
}

function isLocalFilePath(value: string) {
    return /^[a-zA-Z]:[\\/]/.test(value) || value.startsWith('file://');
}

function createUploadKey(ossToken: OssUploadCredentials, key: string) {
    return `${ossToken.prefix}${Date.now()}/${key}`;
}

function joinPublicUrl(domain: string, key: string) {
    return `${domain.replace(/\/+$/, '')}/${key.replace(/^\/+/, '')}`;
}

function getFileExtension(value: string) {
    const cleanValue = value.split('?')[0]?.split('#')[0] ?? value;
    const filename = cleanValue.split('/').pop() ?? '';
    const dotIndex = filename.lastIndexOf('.');
    return dotIndex >= 0 ? filename.slice(dotIndex) : '';
}

async function urlToFile(source: string, fileName: string): Promise<File | null> {
    try {
        const response = await fetch(source);
        if (!response.ok) {
            return null;
        }

        const blob = await response.blob();
        const resolvedType = blob.type || 'application/octet-stream';
        return new File([blob], fileName, { type: resolvedType, lastModified: Date.now() });
    } catch {
        return null;
    }
}

async function resolveUploadSource(source: string | File, fileKey: string): Promise<UploadSourceResult | null> {
    if (source instanceof File) {
        return { file: source };
    }

    if (isRemoteUrl(source)) {
        return { file: new File([], fileKey), sourceUrl: source };
    }

    if (isLocalFilePath(source)) {
        return null;
    }

    const fileName = `${Date.now()}${getFileExtension(source)}`;
    const file = await urlToFile(source, fileName);
    if (!file) {
        return null;
    }

    return { file };
}

function getUploadErrorMessage(source: string | File) {
    if (typeof source === 'string' && isLocalFilePath(source)) {
        return '浏览器端无法直接读取本地文件路径，请改为传入 File、blob URL、data URL 或可访问的资源 URL';
    }

    return '文件读取失败';
}

export async function uploadFile(ossToken: OssUploadCredentials, fileUri: string | File, fileKey: string, retryCount: number = 0, maxRetry: number = 0, progressCb?: UploadProgressCallback, abortSignal?: AbortSignal): Promise<UploadResponse> {
    const resolvedSource = await resolveUploadSource(fileUri, fileKey);
    if (!resolvedSource) {
        return { code: -1, msg: getUploadErrorMessage(fileUri) };
    }

    if (resolvedSource.sourceUrl) {
        return { code: 1, msg: 'ok', data: { url: resolvedSource.sourceUrl } };
    }

    const uploadKey = createUploadKey(ossToken, fileKey);
    const fileRes = await uploadFileToOss(resolvedSource.file, uploadKey, ossToken, progressCb, abortSignal);
    if (fileRes.code !== 1 || !fileRes.data) {
        if (retryCount < maxRetry) {
            return uploadFile(ossToken, fileUri, fileKey, retryCount + 1, maxRetry, progressCb, abortSignal);
        }

        return { code: fileRes.code, msg: fileRes.msg };
    }

    const url = joinPublicUrl(ossToken.domain, fileRes.data.key);
    return { code: 1, msg: 'ok', data: { url } };
}

export async function uploadImage(ossToken: OssUploadCredentials, imageUri: string | File, imageKey: string, retryCount: number = 0, maxRetry: number = 0, progressCb?: UploadProgressCallback, abortSignal?: AbortSignal): Promise<UploadResponse> {
    if (typeof imageUri === 'string' && isRemoteUrl(imageUri)) {
        return { code: 1, msg: 'ok', data: { url: imageUri } };
    }

    const uploadKey = createUploadKey(ossToken, imageKey);
    let imageRes: Awaited<ReturnType<typeof uploadFileToOss>>;
    if (typeof imageUri === 'string' && isDataUrl(imageUri)) {
        imageRes = await uploadForBase64(imageUri, uploadKey, ossToken, progressCb, abortSignal);
    } else {
        const resolvedSource = await resolveUploadSource(imageUri, imageKey);
        if (!resolvedSource) {
            return { code: -1, msg: getUploadErrorMessage(imageUri) };
        }

        if (resolvedSource.sourceUrl) {
            return { code: 1, msg: 'ok', data: { url: resolvedSource.sourceUrl } };
        }

        imageRes = await uploadFileToOss(resolvedSource.file, uploadKey, ossToken, progressCb, abortSignal)
    }

    if (imageRes.code !== 1 || !imageRes.data) {
        if (retryCount < maxRetry) {
            return uploadImage(ossToken, imageUri, imageKey, retryCount + 1, maxRetry, progressCb, abortSignal);
        }

        return { code: imageRes.code, msg: imageRes.msg };
    }

    const url = joinPublicUrl(ossToken.domain, imageRes.data.key);
    return { code: 1, msg: 'ok', data: { url } };
}

export async function uploadJson(ossToken: OssUploadCredentials, jsonText: string, jsonKey: string, retryCount: number = 0, maxRetry: number = 0, progressCb?: UploadProgressCallback, abortSignal?: AbortSignal): Promise<UploadResponse> {
    const uploadKey = createUploadKey(ossToken, jsonKey);
    const jsonRes = await uploadForText(jsonText, uploadKey, ossToken, progressCb, abortSignal);

    if (jsonRes.code !== 1 || !jsonRes.data) {
        if (retryCount < maxRetry) {
            return uploadJson(ossToken, jsonText, jsonKey, retryCount + 1, maxRetry, progressCb, abortSignal);
        }

        return { code: jsonRes.code, msg: jsonRes.msg };
    }

    const url = `${joinPublicUrl(ossToken.domain, jsonRes.data.key)}?v=${Date.now()}`;
    return { code: 1, msg: 'ok', data: { url } };
}
